import React from 'react'
import {
  ButtonVariant,
  MaxWidthConstraint,
  OneByFourGrid,
  OneByThreeGrid,
  OvalButton,
  PhotoHero,
  Spacer,
  WorkArea,
} from '@revelrygroup/components'
import classnames from 'classnames'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import { API } from '../revelry_api'
import { FuneralHome } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import Layout from '../components/Layout'
import { routes } from '../routes'
import styles from '../styles/PlanningAhead.module.scss'
import { Testimonials } from '../components/Testimonials'

interface Props {
  funeralHome: FuneralHome | null
}

const PlanningAhead: React.FC<Props> = ({ funeralHome }) => {
  return (
    <>
      <Head>
        <title>
          Planning Ahead - {funeralHome.name} - {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          content={`Funeral pre-planning can be stressful, but most people feel relieved once they’ve shared their wishes. Our funeral directors will walk you through the pre-planning process so your loved ones don't have to worry.`}
          key="description"
        />
      </Head>

      <Layout>
        <PhotoHero>Planning Ahead</PhotoHero>

        <div className="height-100">
          <main>
            <WorkArea style={{ backgroundColor: 'white' }}>
              <MaxWidthConstraint>
                <div className="centered-text">
                  <Image
                    alt="Kalas Funeral Home logo"
                    height={60}
                    layout="fixed"
                    src="/kalas-radian--blue.png"
                    width={60}
                  />
                </div>

                <Spacer verticalTop="xs">
                  <h3 className={classnames('h3', 'centered-text')}>
                    Most people shy away from talking about death. It&apos;s unsettling to think
                    about our mortality and the fact that we will not always be here. Yet death is
                    inevitable for us all, and planning your funeral celebration is really no
                    different than planning for retirement. It requires an honest conversation, and
                    most people feel relieved once they&apos;ve shared their wishes.
                  </h3>
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <WorkArea className="blue-gradient-background">
              <MaxWidthConstraint maxWidth={975}>
                <h2 className={classnames('h2', 'centered-text')}>Benefits of Planning Ahead</h2>

                <Spacer verticalTop="md">
                  <OneByFourGrid>
                    <div>
                      <div className={styles.gridPhoto}>
                        <Image
                          alt="Family in meadow"
                          height={277}
                          layout="responsive"
                          src="/photo-family-in-meadow@2x.jpg"
                          width={277}
                        />
                      </div>

                      <Spacer vertical="xs">
                        <h6 className="h6">You&apos;ll ease the stress on your family.</h6>
                      </Spacer>

                      <p className="body">
                        When you die and your wishes are not known, your family will be faced with
                        making difficult decisions at a trying time. Confusion and disagreements are
                        common amongst family members when there is no plan to follow.
                      </p>
                    </div>

                    <div>
                      <div className={styles.gridPhoto}>
                        <Image
                          alt="Grandma with flowers"
                          height={277}
                          layout="responsive"
                          src="/photo-grandma-flowers@2x.jpg"
                          width={277}
                        />
                      </div>

                      <Spacer vertical="xs">
                        <h6 className="h6">You can assume financial responsibility.</h6>
                      </Spacer>

                      <p className="body">
                        Planning ahead enables you to make financial arrangements to cover your
                        funeral costs. You&apos;ll be able to determine exactly how much everything
                        will cost and make a plan for how to pay for it.
                      </p>
                    </div>

                    <div>
                      <div className={styles.gridPhoto}>
                        <Image
                          alt="Beer cheers"
                          height={277}
                          layout="responsive"
                          src="/photo-beer-cheers@2x.jpg"
                          width={277}
                        />
                      </div>

                      <Spacer vertical="xs">
                        <h6 className="h6">Your final wishes will be followed.</h6>
                      </Spacer>

                      <p className="body">
                        You may have a preference for burial or cremation, the epitaph on your
                        headstone or the music and readings at your celebration. It also gives you a
                        chance to inject some of your personality into the event.
                      </p>
                    </div>

                    <div>
                      <div className={styles.gridPhoto}>
                        <Image
                          alt="Family dinner"
                          height={277}
                          layout="responsive"
                          src="/photo-family-dinner@2x.jpg"
                          width={277}
                        />
                      </div>

                      <Spacer vertical="xs">
                        <h6 className="h6">You&apos;ll help prevent family conflict.</h6>
                      </Spacer>

                      <p className="body">
                        If you pass away unexpectedly and you haven&apos;t outlined your funeral
                        wishes, your family will be left to guess what you would have wanted, which
                        can often lead to family tension and conflicts.
                      </p>
                    </div>
                  </OneByFourGrid>
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <WorkArea style={{ backgroundColor: 'white' }}>
              <MaxWidthConstraint maxWidth={675}>
                <h2 className={classnames('h2', 'centered-text')}>
                  Is Planning Ahead Right for You?
                </h2>

                <Spacer vertical="sm">
                  <p className={classnames('body', 'centered-text')}>
                    For many, putting together a funeral plan now may sound like a foreign and
                    unnecessary concept, but there are many advantages to sharing and recording your
                    funeral preferences.
                  </p>
                </Spacer>

                <div className="centered-text">
                  <OvalButton as="Link" href={routes.contact} variant={ButtonVariant.DEFAULT_DARK}>
                    Contact Us
                  </OvalButton>
                </div>
              </MaxWidthConstraint>

              <MaxWidthConstraint>
                <Spacer verticalTop="md">
                  <Image
                    alt="Family celebrating"
                    height={575}
                    layout="responsive"
                    src="/photo-sparklers@2x.jpg"
                    width={1024}
                  />
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <WorkArea className="blue-gradient-background">
              <MaxWidthConstraint maxWidth={975}>
                <h2 className={classnames('h2', 'centered-text')}>Memorial Products</h2>

                <h5 className={classnames('h5', 'centered-text')}>
                  Caskets, Vaults, Urns &amp; Keepsakes
                </h5>

                <Spacer verticalTop="sm">
                  <div className="centered">
                    <OneByThreeGrid>
                      <Link href={routes.products}>
                        <a className={classnames(styles.gridPhoto, styles.gridPhotoLink)}>
                          <Image
                            alt="Casket"
                            height={277}
                            layout="responsive"
                            src="/products/caskets/8. UG1 Silver Sapphire.jpg"
                            width={277}
                          />
                        </a>
                      </Link>

                      <Link href={`${routes.products}?tab=vaults`}>
                        <a className={classnames(styles.gridPhoto, styles.gridPhotoLink)}>
                          <Image
                            alt="Vault"
                            height={277}
                            layout="responsive"
                            src="/products/vaults/4-Stainless Steel Triune.jpg"
                            width={277}
                          />
                        </a>
                      </Link>

                      <Link href={`${routes.products}?tab=urns`}>
                        <a className={classnames(styles.gridPhoto, styles.gridPhotoLink)}>
                          <Image
                            alt="Urn"
                            height={277}
                            layout="responsive"
                            src="/products/urns/14. Aristocrat.jpg"
                            width={277}
                          />
                        </a>
                      </Link>
                    </OneByThreeGrid>
                  </div>
                </Spacer>

                <Spacer verticalTop="sm">
                  <div className="centered-text">
                    <OvalButton
                      as="Link"
                      href={routes.products}
                      variant={ButtonVariant.DEFAULT_DARK}
                    >
                      View Our Product Catalog
                    </OvalButton>
                  </div>
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <Testimonials />
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const funeralHome = await API.funeralHomes.load()

    return { props: { funeralHome } }
  } catch (e) {
    return { props: { funeralHome: null } }
  }
}

export default PlanningAhead
